/**
 * qnet.ts — Linear Q-function, fit by closed-form ridge regression.
 *
 * This is the FROZEN-TARGET approximator for Fitted Q-Iteration (see fqi.ts).
 * Why linear (not the old GRU):
 *   - The previous architecture (qnet.ts v1) was a GRU + Q-head trained by
 *     online TD(λ). It diverged — the deadly triad (off-policy + bootstrapping
 *     + non-linear function approximation) made Q-values explode to 1e51, and
 *     eval got WORSE with training. Target networks, δ-clipping, gradient
 *     clipping, λ tuning — none of it saved the architecture.
 *   - A LINEAR Q fit by ridge regression CANNOT diverge: each fit is the
 *     exact minimizer of a convex least-squares problem with a fixed target.
 *     The moving-target instability of off-policy TD is gone.
 *   - On the 4-world × 3-seed validation, linear-FQI beats random by
 *     1.31–1.54× on every (world, seed). The features are informative enough
 *     that the loss of expressiveness vs an MLP doesn't matter; the stability
 *     gain does.
 *
 * Q(s, a) = w · φ(s, a) + b    where φ ∈ ℝ^N_FEATURES (11 features per action).
 * Weights live in a single Float64Array of length N_FEATURES + 1 (last entry
 * is the bias). Predicting one action's Q is a single dot product; predicting
 * all 32 candidates is a matvec over a (32 × 12) feature block.
 */

import { choleskySolve } from './linalg';
import { N_FEATURES } from './features';
import { type VariantSpec, MOB_BASELINE } from './variant';

/** Baseline weight length (features + bias). Per-instance length lives on
 *  the LinearQ instance as `nW` — variant-dependent. */
export const N_W = N_FEATURES + 1;

/** Default L2 regularisation strength for the ridge solve. */
export const DEFAULT_LAMBDA = 1.0;
/** Default discount factor for the bootstrap target r + γ·maxQ'. */
export const DEFAULT_GAMMA = 0.95;

export interface LinearQOptions {
  /** Ridge λ (L2 strength). Larger = more regularisation, smoother Q. */
  lambda?: number;
  /** Discount γ for the FQI target. The agent's effective planning horizon. */
  gamma?: number;
  /** Variant spec — sizes the weight vector. Defaults to the baseline. */
  spec?: VariantSpec;
}

export class LinearQ {
  /** Weight vector — features then bias. Length nW. */
  w: Float64Array;
  /** Ridge regularisation strength (read at fit-time; mutating is safe). */
  lambda: number;
  /** Discount factor (read by fqi.ts when computing bootstrap targets). */
  gamma: number;
  /** Variant spec — fixes the feature/action dimensionality. */
  readonly spec: VariantSpec;
  /** Per-instance feature count (= spec.nFeatures). */
  readonly nf: number;
  /** Per-instance action count (= spec.nActions). */
  readonly nActions: number;
  /** Per-instance weight count (= nf + 1). */
  readonly nW: number;

  constructor(opts: LinearQOptions = {}) {
    this.spec = opts.spec ?? MOB_BASELINE;
    this.nf = this.spec.nFeatures;
    this.nActions = this.spec.nActions;
    this.nW = this.nf + 1;
    this.w = new Float64Array(this.nW);
    this.lambda = opts.lambda ?? DEFAULT_LAMBDA;
    this.gamma = opts.gamma ?? DEFAULT_GAMMA;
  }

  /**
   * Predict Q for one action's feature row. `x` must be length `nf`.
   * Augments x with a trailing 1 (the bias term) and dots with w.
   */
  predict(x: Float64Array): number {
    const nf = this.nf;
    let s = this.w[nf]; // bias
    for (let i = 0; i < nf; i++) s += this.w[i] * x[i];
    return s;
  }

  /**
   * Predict Q for all `nActions` candidates at once.
   * `Xall` is a flat (nActions × nf) block, row-major. Returns a
   * freshly-allocated Float64Array(nActions). Use predictInto() to reuse a
   * buffer.
   */
  predictActions(Xall: Float64Array): Float64Array {
    const out = new Float64Array(this.nActions);
    return this.predictInto(Xall, out);
  }

  /** In-place version of predictActions. `out` must be length `nActions`. */
  predictInto(Xall: Float64Array, out: Float64Array): Float64Array {
    const nf = this.nf;
    const nA = this.nActions;
    const b = this.w[nf];
    for (let a = 0; a < nA; a++) {
      const row = a * nf;
      let s = b;
      for (let i = 0; i < nf; i++) s += this.w[i] * Xall[row + i];
      out[a] = s;
    }
    return out;
  }

  /**
   * Predict Q for every candidate across a BATCH of next-states — used by
   * fqi.ts to compute max_a' Q(s', a') for the bootstrap target. `Xbatch`
   * is (B × nActions × nf) flat; `out` is (B × nActions), filled row-major.
   */
  predictBatch(Xbatch: Float64Array, B: number, out: Float64Array): Float64Array {
    const nf = this.nf;
    const nA = this.nActions;
    const b = this.w[nf];
    const stride = nA * nf;
    for (let k = 0; k < B; k++) {
      const xOff = k * stride;
      const oOff = k * nA;
      for (let a = 0; a < nA; a++) {
        const row = xOff + a * nf;
        let s = b;
        for (let i = 0; i < nf; i++) s += this.w[i] * Xbatch[row + i];
        out[oOff + a] = s;
      }
    }
    return out;
  }

  /**
   * Fit the weights to (X, y) by ridge regression:
   *
   *   w = (AᵀA + λI)⁻¹ Aᵀy,    A = [X | 1]  (the bias column appended).
   *
   * The closed-form solution is exact — there is no learning rate, no
   * iteration, no divergence. The whole "fit a round" call is one Cholesky
   * solve of an (nf+1)×(nf+1) system, which takes ~50µs.
   *
   * `X` is (B × nf) flat; `y` is length B.
   */
  fit(X: Float64Array, y: Float64Array, B: number): void {
    const nf = this.nf;
    const d = this.nW;
    // Build the Gram matrix G = AᵀA + λI  (d×d) and vector AtA_y = Aᵀy.
    const G = new Float64Array(d * d);
    const Ay = new Float64Array(d);
    for (let k = 0; k < B; k++) {
      const off = k * nf;
      for (let i = 0; i < nf; i++) {
        const xi = X[off + i];
        if (xi === 0) continue; // sparse-friendly skip; features are often 0
        const rowOff = i * d;
        for (let j = 0; j < nf; j++) {
          G[rowOff + j] += xi * X[off + j];
        }
        // Cross terms with the bias column (A[nf] = 1):
        G[rowOff + nf] += xi;
        // Aᵀy contribution from feature i.
        Ay[i] += xi * y[k];
      }
      // Bias diagonal: G[nf, nf] += 1*1.
      G[nf * d + nf] += 1;
      // Cross terms (other half — symmetric): G[nf, i] += 1 * X[i].
      const biasRow = nf * d;
      for (let j = 0; j < nf; j++) G[biasRow + j] += X[off + j];
      // Aᵀy bias contribution.
      Ay[nf] += y[k];
    }
    // Add λI (ridge). Don't regularise the bias.
    for (let i = 0; i < nf; i++) G[i * d + i] += this.lambda;

    // Solve G w = Ay via Cholesky. G is SPD (AᵀA is PSD, +λI makes it PD).
    const sol = choleskySolve(G, Ay, d);
    this.w.set(sol);
  }

  /** Reset weights to zero (untrained). */
  reset(): void {
    this.w.fill(0);
  }

  /** Export weights as a plain number[] (for save/JSON). Length nW. */
  exportWeights(): number[] {
    return Array.from(this.w);
  }

  /** Load weights from a number[] of length nW. Throws on mismatch. */
  loadWeights(w: ArrayLike<number>): void {
    if (w.length !== this.nW) {
      throw new Error(
        `loadWeights: expected length ${this.nW}, got ${w.length} — layout drift`,
      );
    }
    for (let i = 0; i < this.nW; i++) this.w[i] = w[i];
  }
}
